import { Box, Grid, Typography } from "@material-ui/core";
import { useState } from "react";
import { ConfirmationDialogRaw } from "../../../../controls/modals/ConfirmationDialogRaw";
import { SimpleDialogRaw } from "../../../../controls/modals/SimpleDialogRaw";
import { LinkCasterProcess } from "../../caster-processes/components/LinkCasterProcess";
import { ProcessData } from "../../models/ProcessData";
import { useCastersContext } from "../CastersContext";
import { BilletTempChart } from "./charts/billet-temp/BilletTempChart";
import { MoldLevelChart } from "./charts/mold-level/MoldLevelChart";
import { StrandSpeedChart } from "./charts/strand-speed/StrandSpeedsChart";
import { HeaderComponent } from "./header/HeaderComponent";
import { useLinkUnlinkProcesses } from "./hooks/useLinkUnlinkProcesses";
import { DeliveryTicketModal } from "./modals/delivery-ticket/DeliveryTicketModal";
import { PourbackModal } from "./modals/pourback-modal/PourbackModal";
import { ElementTable } from "./tables/element-table/ElementTable";
import { HousingTable } from "./tables/housing-table/HousingTable";
import { ProbesTable } from "./tables/probes-table/ProbesTable";
import { ProductionTable } from "./tables/production-table/ProductionTable";
import { ScrapTable } from "./tables/scrap-table/ScrapTable";
import { StrandTable } from "./tables/strand-table/StrandTable";
import { CommentsComponent } from "./ui/CommentsComponent";
import { PourbackFields } from "./ui/PourbackFields";

type Props = {
	reloadProcess: Function;
	calculateProcess: Function;
	refreshData: Function;
};
export const DataEntryPageV2 = ({
	reloadProcess,
	calculateProcess,
	refreshData,
}: Props) => {
	const { state } = useCastersContext();
	const process = state.processData || ({} as ProcessData);
	const tundishShrouds = state.tundishShrouds || [];
	const ladles = state.ladles || [];
	const grades = state.grades || [];
	const tundishes = state.tundishes || [];
	const tonnageReasons = state.tonnageReasons || [];

	const [deliveryTicketModalOpen, setDeliveryTicketModalOpen] = useState(false);
	const [pourbackModalOpen, setPourbackModalOpen] = useState(false);

	const {
		onProcessClicked,
		unlinkCasterProcess,
		showChangeModal,
		showLinkModal,
		showUnlinkModal,
		setShowChangeModal,
		setShowLinkModal,
	} = useLinkUnlinkProcesses({ refreshData });

	return (
		<>
			<Box padding={1} style={{ backgroundColor: "#eaebec" }}>
				<Typography variant="h5" style={{ fontWeight: 600 }}>
					Caster Data Entry
				</Typography>
				<Grid container spacing={2} direction="column" alignItems="center">
					{/* Header Section */}
					<Grid item xs={12} container spacing={1}>
						<HeaderComponent
							process={process}
							ladles={ladles}
							grades={grades}
							tundishes={tundishes}
							tundishShrouds={tundishShrouds}
							tonnageReasons={tonnageReasons}
							reloadProcess={reloadProcess}
							calculateProcess={calculateProcess}
							refreshData={refreshData}
							onProcessClicked={onProcessClicked}
							setDeliveryTicketModalOpen={setDeliveryTicketModalOpen}
						/>
					</Grid>
					{/* Tables Section */}
					<Grid
						item
						xs={12}
						container
						spacing={0}
						justifyContent="space-around"
						alignItems="flex-start"
					>
						{/* Column 1: Housing and Probes Tables */}
						<Grid
							item
							xs={12}
							md={3}
							container
							spacing={1}
							alignContent="flex-start"
						>
							<Grid item xs={12}>
								<HousingTable refreshData={refreshData} />
							</Grid>

							<Grid item xs={12}>
								<ProductionTable
									refresh={refreshData}
									readonly={!!process.HeatId ? false : true}
									key={process.ProcessId}
								/>
							</Grid>

							<Grid item xs={12}>
								<ScrapTable refreshData={refreshData} />
							</Grid>
						</Grid>
						{/* Column 2: Chart, Additional Data and Strand Table */}
						<Grid
							item
							xs={12}
							md={6}
							container
							spacing={1}
							alignContent="flex-start"
							justifyContent="center"
						>
							<Grid item xs={6}>
								<StrandSpeedChart key={process.ProcessId} />
							</Grid>
							<Grid item xs={6}>
								<MoldLevelChart key={process.ProcessId} />
							</Grid>

							<Grid item xs={12}>
								<StrandTable refreshData={refreshData} />
							</Grid>
							<Grid item xs={12} container spacing={1}></Grid>
						</Grid>

						{/* Column 3: Production, Scrap Tables and Delivery Ticket Button */}
						<Grid
							item
							xs={12}
							md={3}
							container
							spacing={1}
							alignContent="flex-start"
						>
							<Grid item xs={12}>
								<BilletTempChart key={process.ProcessId} />
							</Grid>
							<Grid item xs={12}>
								<ProbesTable refreshData={refreshData} />
							</Grid>

							<Grid item xs={12}>
								<PourbackFields
									reloadProcess={reloadProcess}
									refreshData={refreshData}
									setPourbackModalOpen={setPourbackModalOpen}
								/>
							</Grid>
						</Grid>
					</Grid>
					{/* Footer Section */}
					<Grid item xs={12} container spacing={1}>
						<Grid item xs={12}>
							<ElementTable key={process.ProcessId} />
						</Grid>
						<Grid item xs={12}>
							<CommentsComponent
								reloadProcess={reloadProcess}
								refreshData={refreshData}
								key={process.ProcessId}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Box>

			{/* MODALS */}
			<SimpleDialogRaw
				title="Change Modal"
				message={""}
				open={showChangeModal}
				onClose={() => setShowChangeModal(false)}
			/>
			{/* Pourback Modal */}
			<PourbackModal
				show={pourbackModalOpen}
				onHide={(shouldUpdate) => {
					setPourbackModalOpen(false);
					if (shouldUpdate) refreshData();
				}}
				process={process}
			/>
			{process.ProcessId && (
				<DeliveryTicketModal
					show={deliveryTicketModalOpen}
					onHide={(shouldUpdate) => {
						setDeliveryTicketModalOpen(false);
						if (shouldUpdate) refreshData();
					}}
					processId={process.ProcessId}
				/>
			)}
			<ConfirmationDialogRaw
				title="Confirmation"
				message={showUnlinkModal.message}
				open={showUnlinkModal.show}
				onClose={unlinkCasterProcess}
			/>
			<LinkCasterProcess
				show={showLinkModal}
				onHide={(shouldUpdate) => {
					setShowLinkModal(false);
					if (shouldUpdate) reloadProcess();
				}}
				process={process as any}
			/>
		</>
	);
};
